import { callingIdSchema, qualitySchema, skillIdSchema, townIdSchema } from '@wyn/content';
import { z } from 'zod';
import { actionIdSchema, blockViewSchema } from './schemas.ts';

// ---- Chat (/v1/social/chat) ----

/** `realm`, or `town:<id>`. game-core decides who may read and speak in each. */
export const chatChannelSchema = z.string().min(1).max(40);

export const chatMessageSchema = z.object({
  id: z.string(),
  channel: z.string(),
  playerId: z.string(),
  name: z.string(),
  text: z.string(),
  at: z.number(),
  /** A steward took the line down; `text` is empty. Pushed again over the socket when it happens. */
  removed: z.literal(true).optional(),
});
export type ChatMessageView = z.infer<typeof chatMessageSchema>;

// GET /v1/social/chat?channel=&before=&limit=
export const chatRequestSchema = z.object({
  channel: chatChannelSchema,
  before: z.string().max(40).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
/** Oldest first. `next` is the id to pass as `before` for the page before these. */
export const chatResponseSchema = z.object({ channel: z.string(), messages: z.array(chatMessageSchema), next: z.string().nullable() });
export type ChatResponse = z.infer<typeof chatResponseSchema>;

// POST /v1/social/chat
export const sayRequestSchema = z.object({ actionId: actionIdSchema, channel: chatChannelSchema, text: z.string().min(1).max(2000) });
export const sayResponseSchema = z.object({ message: chatMessageSchema });

// ---- Reports (/v1/social/report) ----

export const REPORT_REASONS = ['hate', 'harassment', 'sexual', 'threats', 'spam', 'scam', 'name', 'other'] as const;
export const reportReasonSchema = z.enum(REPORT_REASONS);
export type ReportReason = z.infer<typeof reportReasonSchema>;
/** How each reason reads in the report form. */
export const REPORT_REASON_LABELS: Record<ReportReason, string> = {
  hate: 'Hate',
  harassment: 'Harassment or bullying',
  sexual: 'Sexual content',
  threats: 'Threats',
  spam: 'Spam',
  scam: 'A scam, or trading for real money',
  name: 'An offensive name',
  other: 'Something else',
};
export const REPORT_NOTE_MAX = 500;

/** A line of chat (by channel and id), or a player (by name: Folk, a fight, a trade offer or a fight report). */
export const reportTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('chat'), channel: chatChannelSchema, lineId: z.string().min(1).max(40) }),
  z.object({ kind: z.literal('player'), name: z.string().min(1).max(40) }),
]);
export type ReportTarget = z.infer<typeof reportTargetSchema>;

// POST /v1/social/report
export const reportRequestSchema = z.object({
  actionId: actionIdSchema,
  target: reportTargetSchema,
  reason: reportReasonSchema,
  note: z.string().max(REPORT_NOTE_MAX).optional(),
});
export type ReportRequest = z.infer<typeof reportRequestSchema>;
/** The reporter learns only that it arrived. */
export const reportResponseSchema = z.object({ ok: z.literal(true) });

// ---- Blocks (/v1/social/block) ----

// POST /v1/social/block — by name (from the character sheet, or after a report). The blocked player is not told.
export const blockRequestSchema = z.object({ actionId: actionIdSchema, name: z.string().min(1).max(40) });
// POST /v1/social/unblock
export const unblockRequestSchema = z.object({ actionId: actionIdSchema, playerId: z.string().min(1).max(40) });
/** The blocker's list as it now stands, oldest first. */
export const blocksResponseSchema = z.object({ blocks: z.array(blockViewSchema) });

// ---- The socket ----

/** `realm`, `town:<id>` or `book:<itemKey>:<quality>`; see game-core `parseTopic`. */
export const topicSchema = z.string().min(1).max(80);

/** What the browser sends. Chat goes over HTTP, so it works the same with or without a socket. */
export const socketRequestSchema = z.discriminatedUnion('type', [
  /** Replaces every topic this socket follows. */
  z.object({ type: z.literal('follow'), topics: z.array(topicSchema).max(20) }),
  /** Keeps an idle socket from being closed (API Gateway drops it after 10 quiet minutes). */
  z.object({ type: z.literal('ping') }),
]);
export type SocketRequest = z.infer<typeof socketRequestSchema>;

export const pushedEventSchema = z.object({
  id: z.string(),
  type: z.string(),
  text: z.string(),
  at: z.number(),
  /** The action that wrote it, if a player's request did; the browser that sent it already knows. */
  actionId: z.string().optional(),
});
export type PushedEvent = z.infer<typeof pushedEventSchema>;

/** What the server pushes. Each is a nudge to refetch or a line to show; the HTTP API stays the source of truth. */
export const socketMessageSchema = z.discriminatedUnion('type', [
  /** The topics this socket now follows (some asked for may have been refused). */
  z.object({ type: z.literal('following'), topics: z.array(z.string()) }),
  /** A new entry in your Chronicle: an arrival, a fill, a robbery. */
  z.object({ type: z.literal('event'), event: pushedEventSchema }),
  /** A new line in a channel you follow. */
  z.object({ type: z.literal('chat'), message: chatMessageSchema }),
  /** A book you follow changed. */
  z.object({ type: z.literal('book'), itemKey: z.string(), quality: qualitySchema }),
  /** Someone came to or left a town you follow. */
  z.object({ type: z.literal('presence'), town: townIdSchema }),
  /** A trade offer to or from you was made, answered or lapsed. */
  z.object({ type: z.literal('offers') }),
  z.object({ type: z.literal('error'), message: z.string() }),
]);
export type SocketMessage = z.infer<typeof socketMessageSchema>;

// ---- Leaderboards ----

/** `renown`, `wealth`, or `skill:<skill>` (game-core `BOARDS`). */
export const boardIdSchema = z.union([z.literal('renown'), z.literal('wealth'), z.templateLiteral(['skill:', skillIdSchema])]);
export type BoardIdView = z.infer<typeof boardIdSchema>;

export const boardsRequestSchema = z.object({ board: boardIdSchema.default('renown') });

export const boardEntrySchema = z.object({
  rank: z.number().int(),
  playerId: z.string(),
  name: z.string(),
  calling: callingIdSchema,
  score: z.number(),
  /** Player level for Renown, the skill's level for a skill; null for wealth. */
  level: z.number().nullable(),
});
export type BoardEntryView = z.infer<typeof boardEntrySchema>;

export const boardsResponseSchema = z.object({
  board: boardIdSchema,
  entries: z.array(boardEntrySchema),
  /** The viewer's own score, and their place if they are in the top. */
  you: z.object({ score: z.number(), level: z.number().nullable(), rank: z.number().int().nullable() }),
});
export type BoardsResponse = z.infer<typeof boardsResponseSchema>;
